import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import get from "lodash/get";

import { FieldDescription, FieldGroup } from "@/components/ui/field";
import {
  FormCombobox,
  FormDatePicker,
  FormInput,
  SectionCard,
} from "components/BaseForm";
import CONFIG from "Config";
import CampaignService from "services/Campaign";
import RevenueOpportunityService from "services/RevenueOpportunities";

const toOption = ({ id, name }) => ({ key: id, value: id, text: name });

// A campaign may restrict the statuses through its status preset; without one
// every status applies.
const fetchStatusOptions = async campaignId => {
  if (campaignId) {
    const preset = await CampaignService.getStatusPreset(
      campaignId,
      "revenue_status"
    );
    const presetStatuses = get(preset, "info.revenue_opportunity_statuses");
    if (presetStatuses?.length) return presetStatuses.map(toOption);
  }
  const statuses =
    await RevenueOpportunityService.getRevenueOpportunityStatusesForFilters();
  return statuses.map(toOption);
};

const StatusSection = () => {
  const { setValue } = useFormContext();
  const [campaignId, effectiveDate] = useWatch({
    name: ["campaign_id", "effective_date"],
  });
  const [isStatusChanged, setIsStatusChanged] = useState(false);

  const { data: statusOptions = [] } = useQuery({
    queryKey: ["revenue-opportunity-status-options", campaignId ?? null],
    queryFn: () => fetchStatusOptions(campaignId),
  });

  // A new status takes effect on its own date, so the old one is cleared.
  const handleStatusChange = () => {
    setValue("effective_date", null, { shouldDirty: true });
    setIsStatusChanged(true);
  };

  return (
    <SectionCard title="Status">
      <FieldGroup className="tw:grid tw:grid-cols-1 tw:gap-5 tw:md:grid-cols-2">
        <FormCombobox
          name="status_id"
          label="Status"
          required
          placeholder="Pick a Revenue Opportunity Status"
          options={statusOptions}
          onValueChange={handleStatusChange}
        />
        <div className="tw:flex tw:flex-col tw:gap-2">
          <FormDatePicker
            name="effective_date"
            label="Effective Date"
            required
          />
          {isStatusChanged && !effectiveDate && (
            <FieldDescription>
              Select an effective date for the new status.
            </FieldDescription>
          )}
        </div>
        <FormDatePicker name="launch_date" label="Launch Date" />
        {CONFIG.ENABLE_PIRACY && (
          <FormInput
            name="actionable_machines"
            label="Actionable Machines"
            type="number"
            min={1}
            step={1}
          />
        )}
      </FieldGroup>
    </SectionCard>
  );
};

export default StatusSection;
